import { Provider } from './provider';
import type { RenderContext } from '../render/context/createRenderContext';
import { Point, Rect } from '../lib/region';
import { signal } from '../common-utils/signal';
import { isSafeNumber } from '../common-utils/math';

interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface MouseLikeEvent {
  clientX: number;
  clientY: number;
}

export class ScaleProvider extends Provider {
  scale$ = signal(1);

  get scale() {
    return this.scale$.value;
  }

  // Transform state
  translate$ = signal({
    x: 0,
    y: 0,
  });

  get translateX() {
    return this.translate$.value.x;
  }

  get translateY() {
    return this.translate$.value.y;
  }

  // Cursor position in canvas coordinates
  x: number = 0;
  y: number = 0;

  // Configuration
  isDisabled: boolean = false;
  safeMode: boolean;

  // Internal state
  scaling: boolean = false;
  scaleTimer: ReturnType<typeof setTimeout> | null = null;

  constructor({ safeMode = true }: { safeMode?: boolean } = {}) {
    super();
    this.onWheel = this.onWheel.bind(this);
    this.safeMode = safeMode;
  }

  attach(renderContext: RenderContext): void {
    super.attach(renderContext);
    this.diposable.push(initKeyboardEvent(this));
  }

  onChange(): void {
    this.renderContext.api.markNeedRender();
  }

  dispose(): void {
    super.dispose();
  }

  onWheel(e: WheelEvent): void {
    if (this.isDisabled) return;
    if ((this.renderContext as any).mode === 'presentation') return;
    const DPR = this.renderContext.dpr;

    // 优先检查平移操作（非Ctrl/Cmd键时）
    if (!(e.ctrlKey || e.metaKey)) {
      this.translate(e.deltaX * DPR, e.deltaY * DPR);
      return;
    }

    // Ctrl/Cmd键按下时进行缩放
    const { interactive } = this.renderContext.providers;
    // Improve zoom precision for better user experience
    // Reduce the zoom factor for smoother scaling
    const zoomFactor = e.deltaY > 0 ? 0.95 : 1.05; // Smaller increments for more precise control
    this.setScale(zoomFactor, interactive.x, interactive.y);
  }

  onResize(width: number, nextWidth: number): void {
    const canvas = this.renderContext.canvas.container;
    if (!canvas) return;

    // Calculate the scale ratio based on width change
    const scaleRatio = nextWidth / width;

    // Get canvas center position
    const canvasCenterY = canvas.clientHeight / 2;

    // Calculate current center point in transformed coordinates
    const currentCenterY = (canvasCenterY - this.translateY) / this.scale;

    // Update scale
    this.scale$.set(this.scale * scaleRatio);

    // Adjust translation to keep center point stable
    this.translate$.set({
      x: this.translateX * scaleRatio,
      y: canvasCenterY - currentCenterY * this.scale,
    });

    // Trigger update
    this.onChange();
  }

  updateCursorOffset(event: MouseLikeEvent | null | undefined): void {
    const canvas = this.renderContext.canvas.container;
    if (!canvas || !event) return;
    const DPR = this.renderContext.dpr;

    const rect = canvas.getBoundingClientRect();
    this.x = (event.clientX - rect.left) * DPR;
    this.y = (event.clientY - rect.top) * DPR;
  }

  offsetTransform(point: Point): Point {
    const translateX = this.translateX;
    const translateY = this.translateY;
    const scale = this.scale;
    return {
      x: (point.x - translateX) / scale,
      y: (point.y - translateY) / scale,
    };
  }

  boxTransform(box: Box): Box {
    const translateX = this.translateX;
    const translateY = this.translateY;
    const scale = this.scale;
    return {
      x: box.x * scale + translateX,
      y: box.y * scale + translateY,
      w: box.w * scale,
      h: box.h * scale,
    };
  }

  pointTransform(point: Point): Point {
    const translateX = this.translateX;
    const translateY = this.translateY;
    const scale = this.scale;
    return {
      x: point.x * scale + translateX,
      y: point.y * scale + translateY,
    };
  }

  regionTransform(region: Rect): Rect {
    return region.transform(this.scale, this.translateX, this.translateY);
  }

  setScale(delta: number, x: number, y: number): void {
    const oldScale = this.scale;
    const step = oldScale * 0.023;
    let scale = this.scale;
    scale -= delta * step;
    this.setScaleByOffset(scale, x, y);
  }

  setScaleByOffset(value: number, offsetX: number, offsetY: number): void {
    const oldScale = this.scale;
    this.setScaleRaw(value);
    const newScale = this.scale;
    const x = offsetX;
    const y = offsetY;
    const dx = x * (oldScale - newScale);
    const dy = y * (oldScale - newScale);
    this.translate(dx, dy);
  }

  setZoom(value: number): void {
    const DPR = this.renderContext.dpr;
    this.setScaleByCenter(value * DPR);
  }

  get zoomValue(): number {
    const DPR = this.renderContext.dpr;
    return this.scale / DPR;
  }

  setScaleByCenter(value: number): void {
    const DPR = this.renderContext.dpr;
    const canvas = this.renderContext.canvas.canvas;
    const canvasWidth = canvas.width - (this.renderContext.paddingRight ?? 0) * DPR;
    const offsetX = canvasWidth / 2 / this.scale - this.translateX / this.scale;
    const offsetY = canvas.height / 2 / this.scale - this.translateY / this.scale;
    this.setScaleByOffset(value, offsetX, offsetY);
  }

  setScaleRaw(value: number, translate?: { x?: number; y?: number }): void {
    // Early validation
    if (!value || isNaN(value)) return;

    // Set and clamp scale value
    this.scale$.set(Math.min(12, Math.max(0.05, value)));

    // Update translation if provided
    if (translate) {
      const { x, y } = translate;
      const translateVal: typeof translate = {};
      if (x !== undefined && isSafeNumber(x)) {
        translateVal.x = x;
      }
      if (y !== undefined && isSafeNumber(y)) {
        translateVal.y = y;
      }

      if (Object.keys(translateVal).length > 0) {
        this.translate$.set({
          ...this.translate$.peek(),
          ...translateVal,
        });
      }
    }

    // Handle scaling state and debounced rendering
    this._handleScalingState();

    // Trigger immediate updates
    this.renderContext.api.markNeedRender();
  }

  private _handleScalingState(): void {
    this.scaling = true;

    // Clear existing timer
    if (this.scaleTimer) {
      clearTimeout(this.scaleTimer);
    }

    // Set debounced completion handler
    // Use longer debounce (200ms) to ensure scaling truly stopped before rebuilding caches
    // This prevents premature cache rebuilding during rapid zoom gestures
    this.scaleTimer = setTimeout(() => {
      this.scaling = false;
      this.scaleTimer = null;
      this._performFinalRender();
    }, 200);
  }

  private _performFinalRender(): void {
    const api = this.renderContext.api;
    if (api.paint) {
      // Double render for stability
      api.markNeedRender();
      api.paint();
      api.markNeedRender();
      api.paint();
    }
  }

  translate(x: number, y: number): void {
    this.translate$.set({
      x: this.translateX + (isSafeNumber(x) ? x : 0),
      y: this.translateY + (isSafeNumber(y) ? y : 0),
    });

    const DPR = this.renderContext.dpr;

    if (this.safeMode) {
      const rootNodes = this.renderContext.api.getChildren(null);
      const rect = this.renderContext.api.getNodesRegion(rootNodes);
      const transformed = this.regionTransform(rect);
      const canvas = this.renderContext.canvas.stage;
      if (!canvas) return;
      const padding = 32 * DPR;

      const canvasWidth = canvas.width - (this.renderContext.paddingRight ?? 0) * DPR;
      const canvasHeight = canvas.height;

      // Horizontal constraints - Always allow sliding as long as frame doesn't go completely outside
      const minTranslateX = -transformed.w - rect.x * this.scale + padding; // Keep right edge visible
      const maxTranslateX = canvasWidth - padding - rect.x * this.scale; // Keep left edge visible

      if (x > 0) {
        // Moving right
        if (this.translateX > maxTranslateX) {
          this.translate$.peek().x = maxTranslateX;
        }
      } else if (x < 0) {
        // Moving left
        if (this.translateX < minTranslateX) {
          this.translate$.peek().x = minTranslateX;
        }
      }

      // Vertical constraints - Always allow sliding as long as frame doesn't go completely outside
      const minTranslateY = -transformed.h - rect.y * this.scale + padding; // Keep bottom edge visible
      const maxTranslateY = canvasHeight - padding - rect.y * this.scale; // Keep top edge visible

      if (y < 0) {
        // Moving up
        if (this.translateY < minTranslateY) {
          this.translate$.peek().y = minTranslateY;
        }
      } else if (y > 0) {
        // Moving down
        if (this.translateY > maxTranslateY) {
          this.translate$.peek().y = maxTranslateY;
        }
      }
    }

    this.onChange();
  }
}

function initKeyboardEvent(provider: ScaleProvider): () => void {
  function disableScroll(event: Event): void {
    const e = event as WheelEvent;
    if (e.ctrlKey === true || e.metaKey) {
      event.preventDefault();
    }
  }
  const container = provider.renderContext.canvas.container;
  const DPR = provider.renderContext.dpr;

  const resizeObserver = new ResizeObserver(() => {
    // If stage owns resize, avoid double-resize here
    if ((provider.renderContext as any)?.resizeOwnedByStage) return;
    const canvas = provider.renderContext.canvas.stage;
    if (canvas && canvas.parentNode) {
      const dpr = window.devicePixelRatio;
      const container = canvas.parentNode as HTMLElement;
      const width = canvas.width;
      canvas.width = container.clientWidth * dpr;
      canvas.height = container.clientHeight * dpr;
      provider.onResize(width, canvas.width);
    }
  });

  if (container) {
    resizeObserver.observe(container);
  }

  window.addEventListener('mousewheel', disableScroll, { passive: false });
  window.addEventListener('wheel', disableScroll, { passive: false });
  //firefox
  window.addEventListener('DOMMouseScroll', disableScroll, { passive: false });

  function onWheel(e: WheelEvent): void {
    if (provider.isDisabled) return;
    if ((provider.renderContext as any).mode === 'presentation') return;

    // 优先处理平移操作（非Ctrl/Cmd键时）
    if (!(e.ctrlKey || e.metaKey)) {
      provider.translate(-1 * e.deltaX * DPR, -1 * e.deltaY * DPR);
      return;
    }

    // Ctrl/Cmd键按下时进行缩放
    // Detect if the event is from trackpad or mouse wheel
    const wheelDelta = Math.abs((e as any).wheelDelta);
    const isTrackpad = wheelDelta === 120 ? true : false; //  e.deltaMode === 0 && Math.abs(e.deltaY) < 50;
    const mouseWheelValue = 3;
    const zoomFactor = isTrackpad
      ? e.deltaY * 0.6
      : e.deltaY > 0
        ? mouseWheelValue
        : -1 * mouseWheelValue; // Larger increments for mouse wheel
    const { interactive } = provider.renderContext.providers;
    const point = interactive.eventToPoint(e);
    provider.setScale(zoomFactor, point.x, point.y);
  }

  window.addEventListener('wheel', onWheel);

  const dispose = createTouchZoom(provider);

  return () => {
    resizeObserver.disconnect();
    window.removeEventListener('wheel', onWheel);
    window.removeEventListener('mousewheel', disableScroll);
    window.removeEventListener('wheel', disableScroll);
    window.removeEventListener('DOMMouseScroll', disableScroll);
    dispose();
  };
}

function createTouchZoom(provider: ScaleProvider): () => void {
  let touchStartDistance = 0;
  let touchStartScale = 1;
  let lastTouchX = 0;
  let lastTouchY = 0;
  let touchCenter = { x: 0, y: 0 };
  let isTouching = false;
  const DPR = provider.renderContext.dpr;

  const onTouchStart = (e: TouchEvent): void => {
    if (provider.isDisabled) return;

    if (e.touches.length === 1) {
      // 单指触摸，准备移动
      isTouching = true;
      const touch = e.touches[0];
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;
    } else if (e.touches.length === 2) {
      // 双指触摸，准备缩放
      e.preventDefault();
      isTouching = false;

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // 计算两指间的距离
      touchStartDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );
      touchStartScale = provider.scale;

      // 计算触摸中心点
      touchCenter.x = (touch1.clientX + touch2.clientX) / 2;
      touchCenter.y = (touch1.clientY + touch2.clientY) / 2;

      // 更新光标偏移
      provider.updateCursorOffset({
        clientX: touchCenter.x,
        clientY: touchCenter.y,
      });
    }
  };

  const onTouchMove = (e: TouchEvent): void => {
    if (provider.isDisabled) return;

    if (e.touches.length === 1 && isTouching) {
      // 单指移动画布
      e.preventDefault();

      const touch = e.touches[0];
      const deltaX = touch.clientX - lastTouchX;
      const deltaY = touch.clientY - lastTouchY;

      // 移动画布
      provider.translate(deltaX * DPR, deltaY * DPR);

      // 更新上一次位置
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;
    } else if (e.touches.length === 2) {
      // 双指缩放
      e.preventDefault();

      const touch1 = e.touches[0];
      const touch2 = e.touches[1];

      // 计算当前两指间距离
      const currentDistance = Math.hypot(
        touch2.clientX - touch1.clientX,
        touch2.clientY - touch1.clientY
      );

      // 计算缩放比例
      const scaleRatio = currentDistance / touchStartDistance;
      const newScale = touchStartScale * scaleRatio;

      // 计算新的触摸中心点
      const newTouchCenter = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      // 更新光标偏移到新的触摸中心
      provider.updateCursorOffset({
        clientX: newTouchCenter.x,
        clientY: newTouchCenter.y,
      });

      // 应用缩放，以触摸中心为基准点
      provider.setScaleByOffset(newScale, provider.x, provider.y);

      // 如果触摸中心发生了移动，也要相应地移动画布
      const centerDeltaX = newTouchCenter.x - touchCenter.x;
      const centerDeltaY = newTouchCenter.y - touchCenter.y;

      if (Math.abs(centerDeltaX) > 5 || Math.abs(centerDeltaY) > 5) {
        provider.translate(centerDeltaX * DPR, centerDeltaY * DPR);
        touchCenter = newTouchCenter;
      }
    }
  };

  const onTouchEnd = (e: TouchEvent): void => {
    if (provider.isDisabled) return;

    if (e.touches.length === 0) {
      // 所有手指离开屏幕
      isTouching = false;
      touchStartDistance = 0;
      touchStartScale = 1;
    } else if (e.touches.length === 1) {
      // 从双指变为单指，重新设置单指移动的起始位置
      isTouching = true;
      const touch = e.touches[0];
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;
      touchStartDistance = 0;
    }
  };

  // 添加触摸事件监听
  const canvas = provider.renderContext.canvas.container;
  if (canvas) {
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchend', onTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
  }

  return () => {
    if (canvas) {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchend', onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
    }
  };
}
